import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import "../assets/clients.css";

const VipClientDashboard = () => {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [filteredBlocks, setFilteredBlocks] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    loadDashboardData();
  }, [clientId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/vip`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
        // Load blocks data (mock for now, will be replaced with DataTable API)
        loadBlocksData(data.data.client.id);
      } else {
        throw new Error(data.message || "Failed to load VIP dashboard data");
      }
    } catch (error) {
      console.error("Error loading VIP dashboard:", error);
      setError(error.message);
      toast.error("Error loading VIP dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const loadBlocksData = async (clientId) => {
    try {
      // Mock blocks data - will be replaced with DataTable API call
      const mockBlocks = [
        {
          id: 1,
          name: "Block A",
          monthly_vip: 500.00,
          total_vip: 5000.00,
          outstanding_vip: 2500.00,
          vip_status: 1, // Overdue
        },
        {
          id: 2,
          name: "Block B",
          monthly_vip: 750.00,
          total_vip: 7500.00,
          outstanding_vip: 0.00,
          vip_status: 2, // Paid
        },
        {
          id: 3,
          name: "Block C",
          monthly_vip: 600.00,
          total_vip: 6000.00,
          outstanding_vip: 3000.00,
          vip_status: 1, // Overdue
        },
      ];
      setBlocks(mockBlocks);
      setFilteredBlocks(mockBlocks);
    } catch (error) {
      console.error("Error loading blocks:", error);
    }
  };

  const handleStatusClick = (statusId) => {
    if (selectedStatus === statusId) {
      setSelectedStatus("");
      setFilteredBlocks(blocks);
    } else {
      setSelectedStatus(statusId);
      if (statusId === "") {
        setFilteredBlocks(blocks);
      } else {
        const filtered = blocks.filter((block) => block.vip_status === statusId);
        setFilteredBlocks(filtered);
      }
    }
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", { month: "long" });
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !dashboardData) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <div className="text-center">
              <p>Error: {error || "Failed to load VIP dashboard"}</p>
              <button className="btn btn-primary" onClick={() => navigate("/clients")}>
                Back to Clients
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <ol className="breadcrumb">
        <li><Link to="/">Dashboard</Link></li>
        <li><Link to="/clients">Clients</Link></li>
        <li><Link to={`/clients/${clientId}`}>{dashboardData.client.name}</Link></li>
        <li className="active">VIP</li>
      </ol>

      {/* Activity Overview */}
      <div className="row mt-30">
        <div className="col-md-12">
          <div className="panel-title title-standard">
            <h3>Activity overview</h3>
          </div>
          <div className="panel-group margin-down border-down">
            <div className="col-md-2">
              <div
                className={`activity-box js-vip-activity-box ${selectedStatus === "" ? "active" : ""}`}
                style={{ backgroundColor: selectedStatus === "" ? "#333" : "black", cursor: "pointer" }}
                onClick={() => handleStatusClick("")}
              >
                <p className="activity-value">{dashboardData.allBlocks}</p>
                <p className="activity-title">All</p>
              </div>
            </div>
            <div className="col-md-2">
              <div
                className={`activity-box js-vip-activity-box label-danger ${selectedStatus === 1 ? "active" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusClick(1)}
              >
                <p className="activity-value">{dashboardData.vipOverdueBlocks}</p>
                <p className="activity-title">Overdue</p>
              </div>
            </div>
            <div className="col-md-2">
              <div
                className={`activity-box js-vip-activity-box label-success ${selectedStatus === 2 ? "active" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => handleStatusClick(2)}
              >
                <p className="activity-value">{dashboardData.vipPaidBlocks}</p>
                <p className="activity-title">Paid</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Total VIP Amount */}
      <div className="row">
        <div className="col-xs-12 col-sm-12">
          <div className="saving-box mint">
            <img src="/images/hand-mint.png" alt="VIP" />
            <p className="cash">+{formatCurrency(dashboardData.totalVipOfMonth)}</p>
            <p className="saving-information">
              Total VIP Amount in {formatMonth(dashboardData.now)}
            </p>
          </div>
        </div>
      </div>

      {/* VIP Expenditures Table */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard mt-30">
            <h3>VIP Expenditures of {dashboardData.client.name}</h3>
          </div>
          <div className="panel-group">
            <div className="dataTable_wrapper">
              <table className="table table-hover dt-responsive">
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Monthly VIP</th>
                    <th>Total VIP</th>
                    <th>Outstanding VIP</th>
                    <th className="action-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlocks.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No blocks found
                      </td>
                    </tr>
                  ) : (
                    filteredBlocks.map((block) => (
                      <tr key={block.id}>
                        <td>
                          <Link to={`/blocks/${block.id}`}>
                            <span style={{ fontWeight: 700 }}>{block.name}</span>
                          </Link>
                        </td>
                        <td>{formatCurrency(block.monthly_vip)}</td>
                        <td>{formatCurrency(block.total_vip)}</td>
                        <td>{formatCurrency(block.outstanding_vip)}</td>
                        <td>
                          <Link
                            to={`/vip/pay/${block.id}`}
                            className="btn btn-sm btn-default"
                          >
                            Pay
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VipClientDashboard;





















